import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NotificationsService } from 'angular2-notifications';
import { FormBuilder, Validators } from '@angular/forms';
import { UserService } from '../../services/user.service';
import { OfframpService } from '../../services/offramp.service';
import { Ng4LoadingSpinnerService } from 'ng4-loading-spinner';
import * as moment from 'moment';

const defaults = {
    notif: {
        timeOut: 3000,
        showProgressBar: true,
        pauseOnHover: false,
        clickToClose: true,
        maxLength: 50
    }
}

@Component({
    selector: 'app-add-offramp-trade',
    templateUrl: './add-offramp-trade.component.html',
    styleUrls: ['./add-offramp-trade.component.css']
})
export class AddOfframpTradeDialog implements OnInit {
    element: any = {};
    myForm: any;
    liquidityProviderList: any[] = [];
    exchangeQuoteList: any[] = [];
    isEditMode: boolean = false;
    recordId: string = '';
    today: Date = new Date();
    currentUserDisplay: string = '';
    statusOptions = ['COMPLETED', 'PENDING', 'FAILED', 'REJECTED'];

    constructor(
        public dialogRef: MatDialogRef<AddOfframpTradeDialog>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private notifSer: NotificationsService,
        public fb: FormBuilder,
        public userService: UserService,
        public offrampService: OfframpService,
        private spinnerService: Ng4LoadingSpinnerService
    ) {
        this.element = data.record || {};
        this.isEditMode = !!this.element._id;
        this.recordId = this.element._id || '';

        const loggedInUser = this.userService.getUserDetails();
        console.log(loggedInUser, "loggedInUserloggedInUser")
        this.currentUserDisplay = loggedInUser ? `${loggedInUser.name} (${loggedInUser.email})` : '';

        this.myForm = this.fb.group({
            Date: [this.element.Date ? new Date(this.element.Date) : new Date(), [Validators.required]],
            adminid: [
                this.element.adminid ? (this.element.adminid._id || this.element.adminid) : (loggedInUser ? loggedInUser._id : ''),
                [Validators.required]
            ],
            Type: [this.element.Type || 'SELL', [Validators.required]],
            liquidity_provider_id: [
                this.element.liquidity_provider_id ? (this.element.liquidity_provider_id._id || this.element.liquidity_provider_id) : '',
                [Validators.required]
            ],
            exchange_quote_id: [
                this.element.exchange_quote_id ? (this.element.exchange_quote_id._id || this.element.exchange_quote_id) : ''
            ],
            Rate: [this.element.Rate || 0, [Validators.min(0)]],
            Qty: [this.element.Qty || 0, [Validators.required, Validators.min(0.00000001)]],
            feePercent: [this.element.feePercent !== undefined ? this.element.feePercent : 0.118, [Validators.min(0)]],
            tx_hash: [this.element.tx_hash || ''],
            exchangeTradeStatus: [this.element.exchangeTradeStatus || 'PENDING', [Validators.required]]
        });
    }

    ngOnInit() {
        this.loadExchangeQuoteList();
        // Exchange quote is optional - an admin can pick a liquidity provider
        // and enter a rate directly - so the full provider list always needs
        // to be available, not just once a quote is selected.
        this.loadLiquidityProviderList();
    }

    // `autoSelectProviderId`, when passed, sets the form value and
    // recalculates feePercent for that provider (used when a provider is
    // implied by the selected exchange quote). In edit mode the existing
    // provider is already correct from the record, so it's left alone.
    loadLiquidityProviderList(autoSelectProviderId?: string) {
        const filter = { _csrf: this.data._csrf, limit: 10000, page: 1 };
        this.offrampService.getAllOfframpExchanges(filter).subscribe(
            (response: any) => {
                this.liquidityProviderList = (response && response.data) || [];
                if (autoSelectProviderId) {
                    this.myForm.get('liquidity_provider_id').setValue(autoSelectProviderId);
                    this.onLiquidityProviderChange(autoSelectProviderId);
                }
            },
            () => {
                this.notifSer.error('Error', 'Failed to load liquidity provider list', defaults.notif);
                this.liquidityProviderList = [];
            }
        );
    }

    onLiquidityProviderChange(providerId: string) {
        const provider = this.liquidityProviderList.find(p => p._id === providerId);
        if (provider) {
            this.myForm.get('feePercent').setValue(provider.fees != null ? provider.fees : 0);
        }
    }

    onExchangeQuoteChange(quoteId: string) {
        // Cleared back to "None" - leave the rate/provider as-is so the admin
        // can still enter/keep them manually.
        if (!quoteId) {
            return;
        }

        const quote = this.exchangeQuoteList.find(q => q._id === quoteId);
        if (!quote) {
            return;
        }

        this.myForm.get('Rate').setValue(quote.rate != null ? quote.rate : 0);

        const providerId = quote.liquidity_provider_id ? (quote.liquidity_provider_id._id || quote.liquidity_provider_id) : '';
        if (providerId) {
            this.myForm.get('liquidity_provider_id').setValue(providerId);
            this.onLiquidityProviderChange(providerId);
        }
    }

    loadExchangeQuoteList() {
        const filter = { _csrf: this.data._csrf, limit: 10000, page: 1 };
        this.offrampService.getAllOfframpExchangeQuotes(filter).subscribe(
            (response: any) => {
                this.exchangeQuoteList = (response && response.data) || [];
            },
            () => {
                this.notifSer.error('Error', 'Failed to load exchange quote list', defaults.notif);
                this.exchangeQuoteList = [];
            }
        );
    }

    // Save Form to database
    saveTrade() {
        const { notif } = defaults;

        if (this.myForm.invalid) {
            this.notifSer.error('Error', 'Please fill all required fields', notif);
            return;
        }

        this.spinnerService.show();

        const formValue = this.myForm.getRawValue();
        const formData: any = {
            ...formValue,
            Date: moment(formValue.Date).format('YYYY-MM-DD'),
            _csrf: this.data._csrf
        };

        if (this.isEditMode) {
            formData._id = this.recordId;
            this.offrampService.updateOfframpTrade(formData).subscribe(
                (response) => {
                    this.spinnerService.hide();
                    if (response.status || response.success) {
                        this.notifSer.success('Success', response.message || 'Offramp trade updated successfully', notif);
                        this.dialogRef.close({ success: true, data: response });
                    } else {
                        this.notifSer.error('Error', response.message || 'Failed to update offramp trade', notif);
                    }
                },
                (err) => {
                    this.spinnerService.hide();
                    this.notifSer.error('Error', (err.error && err.error.message) || 'Failed to update offramp trade', notif);
                }
            );
        } else {
            this.offrampService.createOfframpTrade(formData).subscribe(
                (response) => {
                    this.spinnerService.hide();
                    if (response.status || response.success) {
                        this.notifSer.success('Success', response.message || 'Offramp trade created successfully', notif);
                        this.dialogRef.close({ success: true, data: response });
                    } else {
                        this.notifSer.error('Error', response.message || 'Failed to create offramp trade', notif);
                    }
                },
                (err) => {
                    this.spinnerService.hide();
                    this.notifSer.error('Error', (err.error && err.error.message) || 'Failed to create offramp trade', notif);
                }
            );
        }
    }

    onCancel() {
        this.dialogRef.close({ success: false });
    }
}



// WEBPACK FOOTER //
// ./src/app/offramp-trades/add-offramp-trade/add-offramp-trade.component.ts